import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errorMessage } from "@/lib/api";
import type { Screen } from "@/lib/types";

export function ReplaceScreenDialog({ screen, open, onOpenChange, onDone }: { screen: Pick<Screen, "id" | "name" | "status">; open: boolean; onOpenChange: (o: boolean) => void; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"code" | "confirm">("code");
  const [device, setDevice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setCode(""); setStep("code"); setError(""); setDevice({}); } }, [open]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const lookup = () => run(async () => { const { data } = await api.post("/pairing/lookup", { code }); setDevice(data.device_info || {}); setStep("confirm"); });
  const replace = () => run(async () => {
    await api.post(`/screens/${screen.id}/replace`, { code });
    toast.success(`« ${screen.name} » est maintenant associé au nouveau téléviseur`);
    onOpenChange(false); onDone();
  });
  const info = Object.entries(device).map(([k, v]) => `${k} : ${v}`).join(" · ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="replace-screen-dialog" className="border-slate-800 bg-slate-900 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl"><RefreshCw className="h-5 w-5 text-orange-400" />Remplacer le téléviseur</DialogTitle>
          <DialogDescription className="text-slate-400">Lancez l'APK Innova-Sign sur le nouveau téléviseur et saisissez le code à 6 chiffres affiché. « {screen.name} » garde son nom, son groupe et toute sa configuration.</DialogDescription>
        </DialogHeader>
        {step === "code" ? (
          <div className="space-y-6 py-2">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={(v: string) => setCode(v.replace(/\D/g, ""))} data-testid="replace-code-input">
                <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-14 w-12 border-slate-700 bg-slate-950 font-mono text-2xl text-orange-300" />)}</InputOTPGroup>
              </InputOTP>
            </div>
            {error && <p data-testid="replace-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
            <Button data-testid="btn-replace-verify" disabled={code.length !== 6 || busy} onClick={lookup} className="w-full bg-blue-600 text-white hover:bg-blue-500"><KeyRound className="mr-2 h-4 w-4" />{busy ? "Vérification…" : "Vérifier le code"}</Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 text-sm">
              <p className="text-slate-200">Code <span className="font-mono text-orange-300">{code}</span> validé.</p>
              {info && <p className="mt-1 text-xs text-slate-500">Nouvel appareil : {info}</p>}
              <p className="mt-3 text-xs text-amber-300/90">L'ancien téléviseur sera déconnecté immédiatement : son jeton ne sera plus valide.</p>
            </div>
            {error && <p data-testid="replace-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" data-testid="btn-replace-back" disabled={busy} onClick={() => setStep("code")} className="flex-1 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">Retour</Button>
              <Button data-testid="btn-replace-confirm" disabled={busy} onClick={replace} className="flex-1 bg-orange-600 text-white hover:bg-orange-500">{busy ? "Remplacement…" : "Remplacer le téléviseur"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
